import { useMemo } from "react";

import AiChatModal from "components/ai_chat/AiChatModal";
import AiChatPanel from "components/ai_chat/AiChatPanel";
import { ProviderType } from "components/integrations/managed_integrations/types";
import { CustomerDocumentRequest } from "lib/generated/app-service-gql/graphql";
import { useCustomerDocuments } from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { AiContextProvider } from "lib/providers/AiContextProvider";
import {
  CustomerDocumentSortBy,
  CustomerDocumentSortOrder,
  CustomerDocumentTypeFilter,
} from "lib/types/graphQLEnums";

function splitUiPath(uiPath?: string): {
  path: string | undefined;
  fileName: string | undefined;
} {
  if (!uiPath) {
    return { path: undefined, fileName: undefined };
  }

  const parts = uiPath.split("/");

  if (parts.length === 1) {
    return { path: undefined, fileName: parts[0] };
  }

  const fileName = parts.pop(); // removes and returns the last element
  const path = parts.join("/"); // rejoin the rest

  return { path, fileName };
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  provider: ProviderType | "uploads";
  uiPath?: string;
  selectedIds?: string[];
};

export default function ContentLibraryAiChatModal({
  isOpen,
  onClose,
  provider,
  uiPath,
  selectedIds,
}: Props) {
  const request: CustomerDocumentRequest = useMemo(() => {
    const commonReq = {
      documentType: CustomerDocumentTypeFilter.ContentLibrary,
      sortBy: CustomerDocumentSortBy.FolderFirst,
      sortOrder: CustomerDocumentSortOrder.Asc,
      originSource: provider === "uploads" ? undefined : provider,
    };

    if (selectedIds?.length) {
      return {
        ...commonReq,
        ids: selectedIds,
      };
    }
    // Get folder name and path from uiPath so we only get the relevant folder
    const { path, fileName } = splitUiPath(uiPath);
    return {
      ...commonReq,
      textSearch: fileName,
      uiPath: path,
    };
  }, [provider, selectedIds, uiPath]);

  const { data: documents } = useCustomerDocuments(
    request,
    {
      page: 1,
      pageSize: 100,
    },
    {
      enabled: isOpen,
    },
  );

  return (
    <AiChatModal isOpen={isOpen} onClose={onClose}>
      <AiContextProvider
        context={{ resourceName: "Content Library", resourceType: "CONTENT_LIBRARY_QA" }}
      >
        <AiChatPanel
          defaultSelectedSources={documents.map((doc) => doc.id)}
          autoAnswerSuggestedPrompt={false}
          uiText={{
            title: "Ask Stotles AI about your content library",
            subtitle:
              "Hello, I'm Stotles AI assistant trained on all your reference materials. \nAsk me anything.",
            inputPlaceholder: "Ask about your content library",
          }}
          suggestedQuestions={[
            {
              prompt: "Find past answers on...",
            },
            {
              prompt: "Find case studies related to...",
            },
            {
              prompt: "Describe our approach to…",
            },
            {
              prompt: "Find testimonials on…",
            },
          ]}
          sourceSelector={{
            title: "Specify content library sources",
            documentType: CustomerDocumentTypeFilter.ContentLibrary,
            inputPlaceholder: "Search library",
          }}
        />
      </AiContextProvider>
    </AiChatModal>
  );
}
